import * as comlink from 'comlink';
import {
	DbwGetHeadWorkerType, DbwDisposeWorkerType, DbwLoadCardsWorkerType,
	DbwGetCardsByHeadWorkerType,
	DbwRawQueryWorkerType, DbwCheckMissingColumnsWorkerType,
} from '@/worker/db.worker.type';
import { Dictionary } from 'vue-router/types/router';
import {
	PermissionsGroup, FilterableCardName,
	ConditionType, GdbLoadProgress, AccountData, FilterDbSubmission, CardLoadProgress,
} from '@/store/models.def';
import gdb from '@/store/gdbz-v2';
import store from '@/store';
import { DbRef } from '@/worker/db/DbRef';
import gdbx from '@/store/modules/gdbx';
import { StateHistory } from '@/util/stateHistory';

interface PromiseData {
	resolve: () => void,
	reject: (error: Error) => void,
	tag?: string,
}
type PermissionGroupLoadPromises = {
	[T in PermissionsGroup]?: PromiseData[]
};


const worker = comlink.wrap<{
	loadCsvToCards: DbwLoadCardsWorkerType,
	getHead: DbwGetHeadWorkerType,
	getCardsByHead: DbwGetCardsByHeadWorkerType,
	rawQuery: DbwRawQueryWorkerType,
	checkMissingColumns: DbwCheckMissingColumnsWorkerType,
	dispose: DbwDisposeWorkerType,
}>(
	new Worker('./db.worker.ts', {
		type: 'module',
	}),
);

export class DbWorker {
	public loadPromises: PermissionGroupLoadPromises = {};
	public cardLoadProgress: CardLoadProgress = {};
	public currentAccountId = '';

	constructor() {
		store.watch(
			(states, getters) => getters['accountx/currentAccountId'],
			async (newValue, oldValue) => {
				StateHistory.clear();
				if (!newValue || newValue === 'loading') {
					// gdbx.dispose();

				} else if (newValue && newValue !== 'loading') {
					gdbx.dispose();

					// Clear state to prevent _confirmLoaded from returning stale status
					this.cardLoadProgress = {};
					for (const cardType in this.loadPromises) {
						if (this.loadPromises.hasOwnProperty(cardType)) {
							const promises = this.loadPromises[cardType] as PromiseData[];
							for (const promise of promises) {
								promise.reject(new Error('Interrupted'));
							}
						}
					}
					this.loadPromises = {};

					// IMPORTANT: Set currentAccountId BEFORE dispose to prevent race condition.
					// The fullAccountData watcher (below) may fire while we are awaiting dispose().
					// If currentAccountId is not set yet, _loadCsv captures empty accountId and
					// silently aborts when it later detects mismatch (accountId !== this.currentAccountId).
					// This caused intermittent "stuck at loading" bugs on fresh page load.
					//
					// WATCH FOR: If this causes issues with account switching (e.g., mixing data
					// between accounts), the order of operations here may need revisiting.
					// The key invariant is: _loadCsv must always capture a valid accountId.
					this.currentAccountId = newValue;

					// Dispose old tables (uses account-specific names so no race condition)
					await worker.dispose();
				}
			});

		store.watch((states: any, getters) => states.accountx && states.accountx.fullAccountData,
			(newValue: AccountData, oldValue: AccountData) => {
				if (newValue) {
					this._confirmLoaded('system');
					this._confirmLoaded('globalAgents');
					this._confirmLoaded('globalCustomers');
					this._confirmLoaded('globalStocks');
					this._confirmLoaded('globalSuppliers');
					this._confirmLoaded('globalProjects');
					this._confirmLoaded('globalLocations');
					this._confirmLoaded('globalAcc');
				}
			});
	}

	public async loadTable(cardType: FilterableCardName) {
		return this.getHead(cardType);
	}

	public async getHead(cardType: FilterableCardName, filterOptions?: Dictionary<ConditionType>) {
		const accountId = this.currentAccountId;
		try {
			await Promise.all([
				this._confirmLoaded('system'),
				this._confirmLoaded('globalAgents'),
				this._confirmLoaded('globalCustomers'),
				this._confirmLoaded('globalStocks'),
				this._confirmLoaded('globalSuppliers'),
				this._confirmLoaded('globalProjects'),
				this._confirmLoaded('globalLocations'),
				this._confirmLoaded('globalAcc'),
				this._confirmLoaded(cardType)]);
			if (accountId !== this.currentAccountId) { return new DbRef(0); }
			const head = new DbRef(await worker.getHead(cardType, filterOptions));

			// Check for missing columns after creating head (filters are applied later during queries)
			// This is a periodic check - actual detection happens when queries run
			this.checkMissingColumns();

			return head;

		} catch (e) {
			throw e;
		}
	}

	public async getCardsByHead(id: number) {
		return await worker.getCardsByHead(id);
	}
	public async rawQuery(id: number, sql: string) {
		return await worker.rawQuery(id, sql);
	}

	/**
	 * Check for missing columns and update versionIncompatible flag
	 * Call this after data loading to show sync warning if needed
	 */
	public async checkMissingColumns(): Promise<{ hasMissing: boolean; columns: string[] }> {
		const result = await worker.checkMissingColumns();
		if (result.hasMissing) {
			console.error('[DBW] Missing columns detected - database sync required:', result.columns);
			gdbx.updateVersionIncompatible(true);
		}
		return result;
	}
	/**
	 * Load the zip if it hasn't, return nothing if it succeeded or when it succeed
	 * @param zipName zip name
	 */
	private _confirmLoaded(cardType: FilterableCardName) {
		return new Promise<void>((resolve, reject) => {
			if (this.cardLoadProgress[cardType] === undefined ||
				this.cardLoadProgress[cardType] === 'Idle' ||
				this.cardLoadProgress[cardType] === 'Failed') {
				try {
					// _loadCsv is async: it REJECTS rather than throwing synchronously, so the
					// try/catch below cannot see its failure. Without this rejection handler a
					// failed load (e.g. "Download URL for <card>.csv not found.") leaves this
					// promise forever pending, and the widget spins at its loading-text ceiling.
					this._loadCsv(cardType).then(() => {
						resolve();
					}, reject);
				} catch (e) {
					reject(e);
				}
			} else if (this.cardLoadProgress[cardType] === 'Completed') {
				resolve();
			} else {
				if (!this.loadPromises[cardType]) { this.loadPromises[cardType] = []; }
				this.loadPromises[cardType]!.push({ reject, resolve, tag: this.currentAccountId });
			}
		});
	}

	private async _loadCsv(cardType: FilterableCardName) {
		// Mark as loading to prevent duplicate loads
		this._updateLoadProgress(cardType, 'Loading');

		const accountId = this.currentAccountId;
		try {
			const { downloadUrl: csvUrl, parquetUrl } = await gdb.getUrlWithParquet(cardType);
			if (accountId !== this.currentAccountId) { return; }
			const rowCount = await worker.loadCsvToCards(this.currentAccountId, csvUrl, cardType, parquetUrl);
			if (accountId !== this.currentAccountId) { return; }
			this._updateLoadProgress(cardType, rowCount);
			this._updateLoadProgress(cardType, 'Completed');
			if (cardType === 'system') {
				const system = await (new DbRef(await worker.getHead(cardType))).getCards();
				if (accountId !== this.currentAccountId) { return; }
				gdb.processSystem(system);
			} else if (
				cardType === 'globalAgents' ||
				cardType === 'globalStocks' ||
				cardType === 'globalCustomers' ||
				cardType === 'globalProjects' ||
				cardType === 'globalLocations' ||
				cardType === 'globalAcc' ||
				cardType === 'globalSuppliers') {
				const submission: FilterDbSubmission = {};
				submission[cardType] = await (new DbRef(await worker.getHead(cardType))).getCards();
				if (accountId !== this.currentAccountId) { return; }
				gdbx.updateFilterDbs(submission);
			}
			const promises = this.loadPromises[cardType];
			if (promises) {
				for (const promise of promises) {
					promise.resolve();
				}
			}

			// Check for missing columns after each card type loads
			// This will show sync warning if filters use columns not in the CSV
			this.checkMissingColumns();
		} catch (e) {
			// If account changed, silently ignore errors (tables may have been disposed)
			if (accountId !== this.currentAccountId) {
				return;
			}
			const promises = this.loadPromises[cardType];
			if (promises) {
				for (const promise of promises) {
					promise.reject(e);
				}
			}
			this._updateLoadProgress(cardType, 'Failed');
			throw e;
		}
	}

	private _updateLoadProgress(cardType: FilterableCardName, status: GdbLoadProgress) {
		this.cardLoadProgress[cardType] = status;
		gdbx.m_updateCardLoadProgress(this.cardLoadProgress);
	}
}

export default new DbWorker();













